import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Check, Loader2, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading } from "@/components/States";
import { formatSubStatus } from "@/lib/billing";
import { toast } from "sonner";

const TIERS = [
  { id: "basic", name: "BASIC", price: 40, blurb: "Solo mechanic starter" },
  { id: "pro", name: "PRO", price: 50, blurb: "Growing independent garage", highlight: true },
  { id: "premium", name: "PREMIUM", price: 60, blurb: "Multi-tech workshop" },
];

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { setStatus(await api("/billing/status")); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const checkout = async (tier, skipTrial = false) => {
    setBusy(true);
    try {
      const res = await api("/billing/checkout", { method: "POST", body: JSON.stringify({ tier, billing_period: period, skip_trial: skipTrial, promo_code: promo.trim() || undefined }) });
      if (res.demo_mode) { toast.success(res.trial ? "14-day trial started" : "Subscription activated"); load(); }
      else window.location.assign(res.checkout_url);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cancel = async () => { if (!window.confirm("Cancel your subscription at period end?")) return; try { await api("/billing/cancel", { method: "POST" }); toast.success("Will cancel at period end"); load(); } catch (e) { toast.error(e.message); } };
  const resume = async () => { try { await api("/billing/resume", { method: "POST" }); toast.success("Subscription resumed"); load(); } catch (e) { toast.error(e.message); } };
  const goToPlans = () => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (loading) return <Loading />;
  const sub = formatSubStatus(status.status);
  const hasPlan = status.status && status.status !== "none";
  const isCancelled = status.status === "canceled";
  const scheduledCancel = status.cancel_at_period_end && !isCancelled;
  const trialEligible = status.trial_eligible !== false;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Billing & Plan" subtitle="Manage your Pro Garages subscription" />

      {hasPlan && (
        <div className="mb-6 rounded-xl border border-border bg-card p-5" data-testid="current-plan">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Current Plan</p>
              <p className="font-display text-2xl font-bold uppercase text-midnight">{status.plan || "—"} <span className="text-sm text-muted-foreground">· {status.billing_period}</span></p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${sub.tone === "success" ? "bg-success/10 text-success" : sub.tone === "warn" ? "bg-warning-bg text-warning" : sub.tone === "error" ? "bg-danger-bg text-danger" : "bg-secondary text-slate-500"}`}>{sub.label}</span>
          </div>
          {status.current_period_end && <p className="mt-2 text-xs text-muted-foreground">Renews {fmtDate(status.current_period_end)}{status.cancel_at_period_end ? " · cancels at period end" : ""}</p>}
          {isCancelled && (
            <div className="mt-3 rounded-lg border border-danger/30 bg-danger-bg p-3" data-testid="cancelled-notice">
              <p className="text-xs text-danger">Your subscription is cancelled and your account is read-only. Your saved data is safe. Choose a plan below to resume.</p>
            </div>
          )}
          {status.status === "trialing" && status.plan && (
            <div className="mt-3 rounded-lg border border-brand/30 bg-brand/5 p-3">
              <p className="text-xs text-muted-foreground">On a free trial — want to start paying now and skip the remaining trial days?</p>
              <button onClick={() => checkout(status.plan, true)} disabled={busy} data-testid="pay-now-btn"
                className="mt-2 flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Subscribe &amp; pay now
              </button>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            {isCancelled ? (
              <button onClick={() => (status.plan ? checkout(status.plan, true) : goToPlans())} disabled={busy} data-testid="resume-btn" className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Resume subscription</button>
            ) : scheduledCancel ? (
              <button onClick={resume} data-testid="resume-btn" className="rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white">Resume subscription</button>
            ) : (
              <button onClick={cancel} data-testid="cancel-btn" className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-2 text-sm font-bold text-danger">Cancel plan</button>
            )}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="promo-input-row">
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Promotion code</label>
        <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} data-testid="promo-code-input" placeholder="Enter a code (optional)" className="rounded-lg border border-border bg-card px-3 py-2 text-sm uppercase tracking-wide" />
        <span className="text-xs text-muted-foreground">Applied securely at Checkout. A discount never re-starts a used free trial.</span>
      </div>

      <div className="mb-5 inline-flex items-center gap-1 rounded-full border border-border bg-secondary p-1">
        {["monthly", "annual"].map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`billing-toggle-${p}`} className={`rounded-full px-5 py-2 text-xs font-extrabold tracking-widest ${period === p ? "bg-midnight text-white" : "text-slate-500"}`}>{p.toUpperCase()}{p === "annual" ? " (2 MO FREE)" : ""}</button>
        ))}
      </div>

      <div id="plans" className="grid gap-4 lg:grid-cols-3">
        {TIERS.map((t) => {
          const price = period === "annual" ? t.price * 10 : t.price;
          const isCurrent = status.plan === t.id && !isCancelled;
          return (
            <div key={t.id} data-testid={`billing-tier-${t.id}`} className={`rounded-2xl border bg-card p-6 ${t.highlight ? "border-2 border-brand" : "border-border"}`}>
              <p className={`text-[13px] font-extrabold tracking-widest ${t.highlight ? "text-brand" : "text-slate-500"}`}>{t.name}</p>
              <div className="mt-1 flex items-end gap-1"><span className="mb-1 text-xl font-bold text-midnight">£</span><span className="font-display text-5xl font-bold text-midnight">{price}</span><span className="mb-1.5 text-sm text-slate-500">{period === "annual" ? "/yr" : "/mo"}</span></div>
              <p className="mt-1 text-xs italic text-slate-500">{t.blurb}</p>
              {isCurrent ? (
                <button disabled data-testid={`billing-choose-${t.id}`}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-secondary py-3 text-sm font-extrabold tracking-wide text-slate-500">
                  <Check className="h-4 w-4" />CURRENT
                </button>
              ) : (
                <div className="mt-5 flex flex-col gap-2">
                  <button disabled={busy} onClick={() => checkout(t.id, true)} data-testid={`billing-choose-${t.id}`}
                    className={`flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-extrabold tracking-wide disabled:opacity-60 ${t.highlight ? "bg-brand text-white" : "border border-brand text-brand"}`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}{isCancelled ? "RESUBSCRIBE" : "SUBSCRIBE & PAY NOW"}
                  </button>
                  {trialEligible && (
                    <button disabled={busy} onClick={() => checkout(t.id, false)} data-testid={`billing-trial-${t.id}`}
                      className="w-full rounded-lg py-2 text-xs font-bold tracking-wide text-slate-500 hover:text-midnight disabled:opacity-60">
                      Start 14-day free trial
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {!status.checkout_enabled && <p className="mt-4 text-center text-xs text-muted-foreground">{trialEligible ? "Demo mode — selecting a plan starts a 14-day trial instantly (no card required)." : "Demo mode — your free trial has already been used, so selecting a plan activates a paid subscription (no second trial)."}</p>}
    </div>
  );
}
